import { createContext, ReactNode, useCallback, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { QueryKeys } from '~/api/queryKeys';
import { AgentJSONAPI } from '~/api/resources/agent';
import { LoanOfficerJSONAPI } from '~/api/resources/loan_officer';
import { Agents, LoanOfficers } from '~/api/scheme';
import useRightSidebar, { RightSidebarProps } from '~/hooks/useRightSidebar';
import { DEFAULT_PERIOD } from '~/config';
import { PreviewType } from '~/@types/previews';
import useVerificationRequestDialog from '~/hooks/useVerificationRequestDialog';

export type TransactionsEntityScope = 'companies' | 'loan-officers';
export type OpenPreviewCallback = (
  previewType: PreviewType,
  license: string,
  options?: {
    showTransactionsLink: boolean;
    connectionLicense?: string;
    transactionsScope?: TransactionsEntityScope;
  }
) => void;

type DefaultOpenCallbackOptions = {
  showTransactionsLink: boolean;
  transactionsScope: TransactionsEntityScope;
};

export type PreviewsContextProps = {
  agent?: AgentJSONAPI;
  agentPreviewRightSidebar: RightSidebarProps;
  agentResponse: UseQueryResult;
  handleClick: OpenPreviewCallback;
  loanOfficer?: LoanOfficerJSONAPI;
  loanOfficerPreviewRightSidebar: RightSidebarProps;
  loanOfficerResponse: UseQueryResult;
  transactionsEntityLicense?: string;
  transactionsEntityScope?: TransactionsEntityScope;
};

const defaultOpenPreviewCallbackOptions: DefaultOpenCallbackOptions = {
  showTransactionsLink: false,
  transactionsScope: 'loan-officers',
};

const defaultSidebar = {
  isRightSidebarOpen: false,
  onOpenRightSidebar: () => {},
  onCloseRightSidebar: () => {},
};

const initialState: PreviewsContextProps = {
  agent: undefined,
  agentPreviewRightSidebar: defaultSidebar,
  agentResponse: {} as UseQueryResult,
  handleClick: () => {},
  loanOfficer: undefined,
  loanOfficerResponse: {} as UseQueryResult,
  loanOfficerPreviewRightSidebar: defaultSidebar,
  transactionsEntityLicense: undefined,
  transactionsEntityScope: undefined,
};

const PreviewsContext = createContext(initialState);

type PreviewsProviderProps = {
  children: ReactNode;
};

function PreviewsProvider({ children }: PreviewsProviderProps) {
  const loanOfficerPreviewRightSidebar = useRightSidebar();
  const agentPreviewRightSidebar = useRightSidebar();
  const { setPendingRequest } = useVerificationRequestDialog();

  const [agentId, setAgentId] = useState<string>('');
  const agentResponse = useQuery(
    QueryKeys.agent(agentId),
    () =>
      Agents.show({
        id: agentId,
      }),
    {
      onSuccess: (response) => {
        !response.data.error &&
          setPendingRequest(response.data.data.attributes.has_pending_verification);
      },
      enabled: !!agentPreviewRightSidebar.isRightSidebarOpen,
    }
  );
  const openAndSetAgent = useCallback(
    (id: string) => {
      setAgentId(id);
      agentPreviewRightSidebar.onOpenRightSidebar();
    },
    [agentPreviewRightSidebar]
  );
  const agent = agentResponse.data?.data.data;

  const [loanOfficerNmls, setLoanOfficerNmls] = useState('');
  const loanOfficerResponse = useQuery(
    QueryKeys.loanOfficer(loanOfficerNmls, DEFAULT_PERIOD),
    () =>
      LoanOfficers.show({
        nmls: loanOfficerNmls,
        period: DEFAULT_PERIOD,
      }),
    {
      onSuccess: (response) => {
        !response.data.error &&
          setPendingRequest(response.data.data.attributes.has_pending_verification);
      },
      enabled: !!loanOfficerPreviewRightSidebar.isRightSidebarOpen,
    }
  );
  const openAndSetLoanOfficer = useCallback(
    (license: string) => {
      setLoanOfficerNmls(license);
      loanOfficerPreviewRightSidebar.onOpenRightSidebar();
    },
    [loanOfficerPreviewRightSidebar]
  );
  const loanOfficer = loanOfficerResponse.data?.data.data;

  const [transactionsEntityLicense, setTransactionsEntityLicense] = useState<string | undefined>();
  const [transactionsEntityScope, setTransactionsEntityScope] = useState<
    TransactionsEntityScope | undefined
  >();

  const handleClick: OpenPreviewCallback = useCallback(
    (previewType, license, options) => {
      const defaultedOptions = {
        ...defaultOpenPreviewCallbackOptions,
        ...options,
      };

      switch (previewType) {
        case 'loan_officer':
          openAndSetLoanOfficer(license);
          if (defaultedOptions.showTransactionsLink) {
            setTransactionsEntityLicense(defaultedOptions.connectionLicense);
            setTransactionsEntityScope(defaultedOptions.transactionsScope);
          }
          return;
        case 'agent':
          openAndSetAgent(license);
          if (defaultedOptions.showTransactionsLink) {
            setTransactionsEntityLicense(defaultedOptions.connectionLicense);
            setTransactionsEntityScope(defaultedOptions.transactionsScope);
          }
          return;
        case 'seller_agent':
        case 'buyer_agent':
          openAndSetAgent(license);
          if (defaultedOptions.showTransactionsLink) {
            setTransactionsEntityLicense(defaultedOptions.connectionLicense);
            setTransactionsEntityScope(defaultedOptions.transactionsScope);
          }
          return;
      }
    },
    [openAndSetAgent, openAndSetLoanOfficer]
  );

  return (
    <PreviewsContext.Provider
      value={{
        agent,
        agentPreviewRightSidebar,
        agentResponse,
        handleClick,
        loanOfficerResponse,
        loanOfficer,
        loanOfficerPreviewRightSidebar,
        transactionsEntityLicense,
        transactionsEntityScope,
      }}
    >
      {children}
    </PreviewsContext.Provider>
  );
}

export { PreviewsProvider, PreviewsContext };
